import { analytics } from 'analytics'
import { isEmpty } from 'lodash'
import { useState } from 'react'

import { PortfolioViewState } from 'enums/PortfolioViewState'
import { Portfolio, PortfolioLinkType } from 'types'

import EditHoldingsModal from 'components/partials/Portfolio/V2/Modals/EditHoldingsModal'
import { CASH_USD_ASSET_SYMBOL } from 'constants/portfolio.constants'
import { PORTFOLIO_EDIT_ASSETS_CLICKED } from 'constants/track.constants'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSCard from 'designSystem/components/PDSCard/PDSCard'
import PDSCardHeader from 'designSystem/components/PDSCardHeader/PDSCardHeader'
import PDSControlGroup from 'designSystem/components/PDSControlGroup/PDSControlGroup'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSHeader from 'designSystem/components/PDSHeader/PDSHeader'
import PDSSearchBar from 'designSystem/components/PDSSearchBar/PDSSearchBar'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'
import { openEditPortfolioAssets } from 'store/modalsSlice'
import { useAppDispatch } from 'store/storeHooks'
import {
  getPortfolioLinkType,
  getPortfolioViewState,
} from 'utils/portfolioUtil'

import BaseAssetList from './BaseAssetList'

type AssetSortType = 'total' | 'change' | 'ticker'

type Props = {
  portfolio: Portfolio
}

const SORT_OPTIONS = [
  { value: 'total', label: 'Total Value' },
  { value: 'change', label: '% Change' },
  { value: 'ticker', label: 'Ticker' },
]

export default function PortfolioAssetsCard({ portfolio }: Props) {
  const dispatch = useAppDispatch()
  const [sortField, setSortField] = useState<AssetSortType>('total')
  const [search, setSearch] = useState<string>('')

  const { assets, id } = portfolio

  // Initial assets
  const unsupportedAssets = portfolio?.unsupportedAssets ?? []
  const cashEquivalentAssets = assets?.filter(
    (asset) => asset.alternativeAssetSymbol === CASH_USD_ASSET_SYMBOL
  )
  const supportedAssets = assets?.filter(
    (a) => a.alternativeAssetSymbol !== CASH_USD_ASSET_SYMBOL
  )

  const linkType = getPortfolioLinkType(portfolio)
  const portfolioViewState = getPortfolioViewState(portfolio)
  const isNormalOrEmpty =
    portfolioViewState === PortfolioViewState.NORMAL ||
    portfolioViewState === PortfolioViewState.EMPTY

  const isThirdPartyManaged = portfolio?.isThirdPartyManaged ?? false
  const isLinkedPortfolio = linkType !== PortfolioLinkType.None
  const showEditHoldingsButton =
    !isThirdPartyManaged && !isLinkedPortfolio && isNormalOrEmpty

  const handleEditAssets = () => {
    dispatch(openEditPortfolioAssets(portfolio))
    analytics.track(PORTFOLIO_EDIT_ASSETS_CLICKED)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  return (
    <>
      <PDSCard display="flex" flexDirection="column">
        <PDSCardHeader>
          <PDSBox>
            <PDSFlexBox alignItems="center" justifyContent="space-between">
              <PDSFlexBox alignItems="center" gap={SIZE.s16}>
                <PDSHeader variant="h4">Positions</PDSHeader>
                {showEditHoldingsButton && (
                  <PDSFlexBox justifyContent="flex-end">
                    <PDSButton
                      onClick={handleEditAssets}
                      size="sm"
                      testId="edit-holdings"
                      text="Edit Holdings"
                      variant="tertiary"
                    />
                  </PDSFlexBox>
                )}
              </PDSFlexBox>
              {isNormalOrEmpty && (
                <PDSBox>
                  <PDSControlGroup
                    mainOnClick={(o: any) => {
                      setSortField(o.value)
                    }}
                    defaultValue={sortField}
                    defaultChecked
                    size="sm"
                    label="View by"
                    options={SORT_OPTIONS}
                  />
                </PDSBox>
              )}
            </PDSFlexBox>
          </PDSBox>
        </PDSCardHeader>

        <PDSBox marginBottom={SIZE.s24}>
          <PDSSearchBar
            border={false}
            onChange={handleSearch}
            placeholder="Search here..."
            size="md"
          />
        </PDSBox>

        <PDSFlexBox flexDirection="column" gap={SIZE.s24} minHeight="12rem">
          {!isEmpty(unsupportedAssets) && isNormalOrEmpty && (
            <PDSBox minHeight="8.25rem">
              <BaseAssetList
                initialAssets={unsupportedAssets}
                portfolioId={id}
                search={search}
                sortField={sortField}
                title="Unsupported"
                titleColor={COLOR.textAlert}
              />
            </PDSBox>
          )}

          {isNormalOrEmpty && (
            <PDSBox minHeight="8.25rem">
              <BaseAssetList
                initialAssets={supportedAssets}
                portfolioId={id}
                search={search}
                sortField={sortField}
                title="Assets"
              />
            </PDSBox>
          )}

          {!isEmpty(cashEquivalentAssets) && isNormalOrEmpty && (
            <PDSBox minHeight="8.25rem">
              <BaseAssetList
                initialAssets={cashEquivalentAssets}
                portfolioId={id}
                search={search}
                sortField={sortField}
                title="Cash Equivalents"
              />
            </PDSBox>
          )}
        </PDSFlexBox>
      </PDSCard>
      <EditHoldingsModal />
    </>
  )
}
